import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import {
  CASE_STATUS_KEYS,
  CASE_STATUS_TONE,
  needsDispatched,
  ORDER_STATE_LABEL,
  ORIGINS,
  ORIGIN_LABEL,
  STAGE_LABEL,
  STAGE_ORDER,
  caseStatus,
  isLate,
  matchesSearch,
  type CaseStatusKey,
  type JobOrder,
  type Origin,
  type Stage,
  type Tone,
  caseFinished,
  DUE_LABEL,
  DUE_TONE,
  URGENT_OVERDUE_LABEL,
  dueKey,
  type DueKey,
} from '@/domain';
import { ORDER_DUES, type OrderDue } from '@/application/ports/JobOrderRepository';
import { useJobOrders } from '@/application/usecases/useOrders';
import { useSession } from '@/application/usecases/useSession';
import { useTableOrder } from '@/application/usecases/useTableOrder';
import { useTableLayout } from '@/application/usecases/useTableLayout';
import { PAGE_SIZES, useTablePage } from '@/application/usecases/useTablePage';
import { strings } from '@/shared/strings';
import { labToday } from '@/shared/format/instant';
import { CaseTable } from '@/ui/data/CaseTable';
import { CASE_STATUS_LABEL } from '@/ui/data/shippingLabels';
import { ORIGIN_SORT_KEYS, casePresets } from '@/ui/data/casePresets';
import { orderColumns, seesCaseValue } from '@/ui/data/orderColumns';
import { DeliveryDate } from '@/ui/data/DeliveryDate';
import { SortSelect } from '@/ui/data/SortSelect';
import { FilterBar, type FilterDef } from '@/ui/data/FilterBar';
import type { Column } from '@/ui/data/ResponsiveTable';
import { StatusWithNote } from '@/ui/data/StatusDot';
import { Drawer } from '@/ui/layout/Drawer';
import { ListFreshness } from '@/features/shell/ListFreshness';
import { ReviewCleanupNotice } from '@/features/payments/ReviewCleanupNotice';
import screen from './OrdersScreen.module.css';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { OrderCaseView } from './OrderCaseView';
import { SelectionActions } from '@/features/bulk/SelectionActions';
import {
  commonStageOf,
  usePermittedActions,
  type SelectionAction,
} from '@/features/bulk/permittedActions';
import { useSelection } from '@/ui/data/useSelection';
import { AdminFigures, type AdminFigure } from './AdminFigures';

/**
 * What a selection on the orders list can have done to it. Section 4 of the
 * request of 24 September 2026: "delete all, archive all, assign all to a
 * particular department or person".
 */
const ORDER_ACTIONS: readonly SelectionAction[] = ['assign', 'archive', 'delete'];
/** The key the Due filter has in the list's address: `/orders?due=overdue`. */
const DUE_PARAM = 'due';
/**
 * The keys of the Status filter and of Urgent only in the address, asked of the
 * server: the admin's figures open this list narrowed by them, `?status=held`
 * and `?urgent=1`. The lab's request of 17 October 2026, item 5.
 */
const STATUS_PARAM = 'status';
const URGENT_PARAM = 'urgent';
/** What the Status filter can be: every status the column reads, the active cases, or all of them. */
type StatusChoice = CaseStatusKey | 'active' | 'all';
const orderKey = (order: JobOrder) => order.jobOrderNo;

/**
 * Orders. Section 5.2.
 *
 * Every case still in the lab: a dispatched order leaves this list the moment
 * it is dispatched and appears in the Archive. The server excludes them by
 * default, so this screen does not filter them out itself and cannot drift from
 * that. Not every case here is active: since the lab's request of 17 October
 * 2026, item 3, a case in Ready or Logistics is finished, and the Active cases
 * status narrows the list to the rest.
 *
 * The search and the stage are filtered here, over the page in hand: a round
 * trip per keystroke would be slower and no more correct. The status, Urgent
 * only, Due and the origin are asked of the server, because the list is paged.
 * The filter bar looks and behaves the same either way.
 */
export function OrdersScreen() {
  const { jo } = useParams<{ jo: string }>();
  const navigate = useNavigate();
  /*
   * A page at a time, from the server. Section 10 of the request of
   * 16 September 2026: this asked for the two hundred cap and rendered the lot,
   * so a lab with three hundred active cases lost a hundred off the end with
   * nothing on screen to say so.
   */
  const paging = useTablePage('orders');
  // The columns as this reader laid them out, kept on their account. The request of 7 October 2026, section 3.
  const layout = useTableLayout('orders');
  // A case's value is sent only to a holder of pay or admin, and nobody else is offered the column.
  const seesValue = seesCaseValue(useSession().data?.permissions);

  const [lastOpened, setLastOpened] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [stage, setStage] = useState<Stage | 'all'>('all');
  // The lab's day, which Overdue, Due today and Due tomorrow are measured against.
  const today = labToday();
  /*
   * Due today, or overdue: asked of the SERVER, and kept in the address. The
   * request of 7 October 2026, sections 6 and 7. It was "Overdue only", a
   * switch over the page in hand, which could not say how many cases there
   * were and found none of the late ones on page two. In the address, because
   * the Overview's Due and Overdue figures open this list narrowed to exactly
   * the cases they counted, and a link to it is a thing to send.
   */
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const asked = params.get(DUE_PARAM);
  const due: OrderDue | 'any' = ORDER_DUES.find((each) => each === asked) ?? 'any';
  const setDue = (next: OrderDue | 'any') =>
    setParams(
      (current) => {
        const address = new URLSearchParams(current);
        if (next === 'any') address.delete(DUE_PARAM);
        else address.set(DUE_PARAM, next);
        return address;
      },
      { replace: true },
    );
  const setParam = (name: string, value: string | null) =>
    setParams(
      (current) => {
        const address = new URLSearchParams(current);
        if (value === null) address.delete(name);
        else address.set(name, value);
        return address;
      },
      { replace: true },
    );
  /*
   * The status and Urgent only, asked of the server and kept in the address,
   * as Due is: a page holds every case of a status, and not the few of it on
   * the page in hand. Active is a choice of its own: still being made, so
   * neither Ready nor Logistics. The lab's request of 17 October 2026, items 3
   * and 5.
   */
  const askedStatus = params.get(STATUS_PARAM);
  const status: StatusChoice =
    askedStatus === 'active' ? 'active' : (CASE_STATUS_KEYS.find((key) => key === askedStatus) ?? 'all');
  const setStatus = (next: StatusChoice) => setParam(STATUS_PARAM, next === 'all' ? null : next);
  const urgentOnly = params.get(URGENT_PARAM) === '1';
  const setUrgentOnly = (on: boolean) => setParam(URGENT_PARAM, on ? '1' : null);
  // Which of the admin's figures counts the list on screen, so it reads as pressed.
  const figure: AdminFigure | null =
    status === 'active' && !urgentOnly && due === 'any'
      ? 'active'
      : status === 'held' && !urgentOnly && due === 'any'
        ? 'held'
        : urgentOnly && status === 'all' && due === 'any'
          ? 'urgent'
          : due === 'overdue' && status === 'all' && !urgentOnly
            ? 'overdue'
            : null;
  // Asked of the server, as the list is paged. Request of 5 October 2026.
  const [origin, setOrigin] = useState<Origin | 'all'>('all');

  /*
   * A dispatched order leaves this list for the archive, which is right for a
   * list of work in hand and wrong the moment somebody asks it for every case
   * that is shipped. So it is fetched only while one of those statuses is
   * chosen, and the list a reader sees by default is the list they saw before.
   */
  const wantsGone = status !== 'all' && status !== 'active' && needsDispatched(status);

  /*
   * Urgent first is the default because it is the order the server already
   * sends: urgent, then the delivery date. Section 3.2 of the request of
   * 13 September 2026 is explicit that a manager who has never touched the
   * control sees what they saw yesterday.
   *
   * The three origin sorts follow, section 2 of the request of 24 September
   * 2026. They are asked of the SERVER, because this list is paged: sorted
   * here, one origin would come first on page one and again on page two.
   */
  const presets = useMemo(
    () =>
      casePresets<JobOrder>([
        // Newest first unless the reader chose otherwise. Request of 5 October 2026, section 2.
        'newestFirst',
        'urgentFirst',
        'dueSoonest',
        'doctorAtoZ',
        ...ORIGIN_SORT_KEYS,
      ]),
    [],
  );
  const order = useTableOrder('orders', presets);

  const orders = useJobOrders(
    useMemo(
      () => ({
        page: paging.page,
        pageSize: paging.pageSize,
        ...(wantsGone ? { includeDispatched: true } : {}),
        ...(origin === 'all' ? {} : { origin }),
        ...(due === 'any' ? {} : { due }),
        ...(status === 'all' ? {} : { status }),
        ...(urgentOnly ? { urgent: true } : {}),
        ...(order.sortParam === undefined ? {} : { sort: order.sortParam }),
      }),
      [paging.page, paging.pageSize, wantsGone, origin, due, status, urgentOnly, order.sortParam],
    ),
  );
  const { data, isPending, isError } = orders;

  const open = jo ?? lastOpened;

  /*
   * The cases ticked for an action on all of them, across pages. Boxes only for
   * a reader who may take one of the actions: a box that can do nothing is a
   * question with no answer. Section 4 of the request of 24 September 2026.
   */
  const actions = usePermittedActions(ORDER_ACTIONS);
  const selection = useSelection(orderKey);

  const visible = useMemo(
    () =>
      // Read inside the callback: `data?.items ?? []` outside it is a new array
      // on every render, which would make this memo do nothing at all.
      (data?.items ?? []).filter(
        (order) =>
          // The doctor and patient names are ABSENT for a caller without
          // seeIdentity, and an absent field simply never matches.
          matchesSearch(query, [
            order.jobOrderNo,
            // The number on the doctor's paper finds the case too. The lab's request of 17 October 2026, item 7.
            order.serialNumber,
            order.dentistName,
            order.patientName,
            ORIGIN_LABEL[order.origin],
          ]) &&
          (stage === 'all' || order.currentStage === stage),
      ),
    [data, query, stage],
  );

  const filtering =
    query.trim() !== '' ||
    stage !== 'all' ||
    status !== 'all' ||
    origin !== 'all' ||
    due !== 'any' ||
    urgentOnly;

  /*
   * Page three of the old list is nothing in the new one, so every control that
   * changes what the list IS puts the reader back on page one. The order counts:
   * the rows on page three of Urgent first are not the rows on page three of
   * Due soonest.
   */
  const { reset } = paging;
  useEffect(() => {
    reset();
  }, [reset, query, stage, status, origin, due, urgentOnly, order.presetKey, order.column]);

  const filters: FilterDef[] = [
    {
      kind: 'search',
      label: strings.orders.search,
      placeholder: strings.orders.searchPlaceholder,
      value: query,
      onChange: setQuery,
    },
    {
      kind: 'select',
      label: strings.orders.stage,
      value: stage,
      options: [
        { value: 'all', label: strings.orders.allStages },
        ...STAGE_ORDER.map((key) => ({ value: key, label: STAGE_LABEL[key] })),
      ],
      onChange: (next) => setStage(next as Stage | 'all'),
    },
    {
      /*
       * Every status the column beside it can show, which is what the client
       * asked for: it offered four order states while the column showed nine,
       * so a reader could see a row saying Shipped and had no way to ask for
       * every row saying Shipped. Section 6 of the request of
       * 16 September 2026.
       */
      kind: 'select',
      label: strings.orders.status,
      value: status,
      options: [
        { value: 'all', label: strings.orders.allStatuses },
        // Still being made: neither Ready nor Logistics. The lab's request of 17 October 2026, item 3.
        { value: 'active', label: strings.orders.activeCases },
        ...CASE_STATUS_KEYS.map((key) => ({ value: key, label: CASE_STATUS_LABEL[key] })),
      ],
      onChange: (next) => setStatus(next as StatusChoice),
    },
    {
      // Local, international or online. Request of 5 October 2026.
      kind: 'select',
      label: strings.table.origin,
      value: origin,
      options: [
        { value: 'all', label: strings.archive.allOrigins },
        ...ORIGINS.map((key) => ({ value: key, label: ORIGIN_LABEL[key] })),
      ],
      onChange: (next) => setOrigin(next as Origin | 'all'),
    },
    {
      // Asked of the server, by the one rule every count reads. See `due` above.
      kind: 'select',
      label: strings.orders.due,
      value: due,
      options: [
        { value: 'any', label: strings.orders.anyDue },
        { value: 'today', label: DUE_LABEL.today },
        { value: 'overdue', label: DUE_LABEL.overdue },
      ],
      onChange: (next) => setDue(next as OrderDue | 'any'),
    },
    { kind: 'toggle', label: strings.orders.urgentOnly, value: urgentOnly, onChange: setUrgentOnly },
  ];

  const columns: Column<JobOrder>[] = [
    {
      /*
       * Where the case is and who has it, in place of a seven segment pipeline.
       * Somebody chasing an order wants to know whose bench to walk to, not how
       * far along it is, and the pipeline answered the second question only.
       */
      key: 'currentStage',
      label: strings.orders.withWhom,
      sortable: true,
      sortValue: (order) => order.currentStage ?? '',
      render: (order) =>
        order.currentStage === null ? (
          <span className={screen.hidden}>{strings.orders.dispatchedAway}</span>
        ) : (
          <span className={screen.holder}>
            <b>{STAGE_LABEL[order.currentStage]}</b>
            <span>{order.holderName ?? strings.orders.nobodyYet}</span>
          </span>
        ),
    },
    {
      /*
       * Where the case comes from, so the origin sorts have something on the
       * row to show for themselves. Section 2 of the request of 24 September
       * 2026. The header sorts it in the client's order, not the alphabet's.
       */
      key: 'origin',
      label: strings.table.origin,
      sortable: true,
      sortValue: (order) => ORIGINS.indexOf(order.origin),
      render: (order) => ORIGIN_LABEL[order.origin],
    },
    {
      /*
       * The date, in its tone, with the countdown under it while the case is
       * still on the floor. It used to be one or the other: the countdown for a
       * case in production and the bare date once it had left, so the busiest
       * list in the lab never showed the delivery date of a case anybody could
       * still do anything about. It does not pulse; a table of pulsing cells is
       * noise, and the status column carries the tone here. Section 4.
       */
      key: 'daysLeft',
      label: strings.orders.due,
      nowrap: true,
      sortable: true,
      sortValue: (order) => order.daysLeft,
      render: (order) => (
        <span className={screen.due}>
          <DeliveryDate date={order.deliveryDate} finished={caseFinished(order)} />
          {order.state === 'production' && (
            <span>
              {order.daysLeft}
              {strings.orders.daysShort}
            </span>
          )}
        </span>
      ),
    },
    /*
     * What the rows carry and the table does not show as it comes. The request
     * of 8 October 2026 (second), and the lab's request of 11 October 2026 for
     * more of them: the day a case was received first, then its dates and how
     * long it has been here, then what it is, then who it is for and its money.
     */
    ...orderColumns([
      'createdAt',
      'startDate',
      'inLab',
      'inStage',
      'caseType',
      'product',
      'teeth',
      'units',
      'shade',
      'clinic',
      ...(seesValue ? (['value'] as const) : []),
      'paidAt',
      'serialNumber',
      'redoOf',
      'noteCount',
      'photoCount',
    ]),
  ];

  return (
    <div>
      <div className={screen.head}>
        <span className={screen.lbl}>{strings.orders.label}</span>
        <h2>{strings.orders.title}</h2>
        <span className={screen.hint}>{strings.orders.hint}</span>
        <span className={screen.spacer} />
        <ListFreshness query={orders} />
      </div>

      {/* Management reads cases here and does not hold Payments, so the notice is here for them. */}
      <ReviewCleanupNotice />

      {/* Active, urgent, overdue and on hold, for the admin. The lab's request of 17 October 2026, item 5. */}
      <AdminFigures screen="orders" current={figure} />

      <FilterBar
        label={strings.orders.filtersLabel}
        filters={filters}
        sort={
          <SortSelect presets={presets} value={order.presetKey} onChange={order.choose} />
        }
      />

      {isPending && <SkeletonRows rows={6} label={strings.common.loadingOrders} />}
      {isError && <p className={screen.hint}>{strings.errors.server}</p>}

      {!isPending && !isError && (
        <CaseTable
          customize={layout}
          columns={columns}
          rows={order.apply(visible)}
          selection={actions.length > 0 ? selection : undefined}
          sort={order.column}
          onSortChange={order.sortByColumn}
          identity={(order) => ({
            jobOrderNo: order.jobOrderNo,
            urgent: order.urgent,
            // The mark of a remake, beside the urgent dot when a case is both.
            // The request of 6 October 2026 (second), item 2.
            redo: order.redoOf !== null,
            held: order.hold.held,
            dentistName: order.dentistName,
            patientName: order.patientName,
          })}
          // Marked, not hidden: a waiting deletion stops nothing but dispatch.
          // Marked, not hidden: a waiting deletion stops nothing but dispatch.
          status={(order) => {
            const note =
              order.deletion.status === 'pending' ? strings.deletion.awaitingApproval : null;
            /*
             * The same function the filter above reads, so the column can never
             * show a status the filter cannot ask for.
             *
             * A case still being made keeps its sharper wording -- Urgent,
             * Overdue, Due in two days -- because on the floor that is the
             * question, and all three are the one status `production`, which is
             * what the filter asks for.
             */
            const key = caseStatus(order);
            return key === 'production' ? (
              <StatusWithNote tone={toneFor(order, today)} note={note}>
                {labelFor(order, today)}
              </StatusWithNote>
            ) : (
              <StatusWithNote tone={CASE_STATUS_TONE[key]} note={note}>
                {CASE_STATUS_LABEL[key]}
              </StatusWithNote>
            );
          }}
          statusSortValue={(order) => CASE_STATUS_KEYS.indexOf(caseStatus(order))}
          paging={{
            page: paging.page,
            pageSize: paging.pageSize,
            total: data?.total ?? 0,
            sizes: PAGE_SIZES,
            onPage: paging.goTo,
            onPageSize: paging.choose,
            label: strings.table.pagesLabel,
          }}
          rowKey={(order) => order.jobOrderNo}
          caption={strings.orders.caption}
          emptyHeading={filtering ? strings.table.noMatch : strings.orders.emptyHeading}
          emptyDetail={filtering ? strings.table.noMatchDetail : strings.orders.emptyDetail}
          onRowClick={(order) => {
            setLastOpened(order.jobOrderNo);
            void navigate({ pathname: `/orders/${order.jobOrderNo}`, search: location.search });
          }}
        />
      )}

      {/* After the table, so the bar appearing moves no row under the pointer. */}
      <SelectionActions
        count={selection.count}
        jobOrderNos={selection.rows.map((row) => row.jobOrderNo)}
        assignTarget={{ jobOrderNos: selection.rows.map((row) => row.jobOrderNo) }}
        commonStage={commonStageOf(selection.rows.map((row) => row.currentStage))}
        actions={actions}
        onClear={selection.clear}
        onSelectPage={
          visible.every((row) => selection.selected.has(row.jobOrderNo))
            ? undefined
            : () => selection.onTogglePage(visible, true)
        }
      />

      <OrderDrawer
        jobOrderNo={jo}
        open={open !== null && jo !== undefined}
        onClose={() => {
          setLastOpened(null);
          void navigate({ pathname: '/orders', search: location.search });
        }}
      />
    </div>
  );
}

/**
 * Urgent wins, then the due date by the one rule: Overdue in red once the date
 * has passed, Due today and Due tomorrow in amber. An order that is past
 * production reads as its own state instead. Overdue is the lists' one rule, so
 * the Overdue only filter shows exactly the orders this calls overdue. The
 * request of 7 October 2026, section 6.
 *
 * An order that is urgent and overdue says both. The request of 8 October 2026.
 */
function toneFor(order: JobOrder, today: string): Tone {
  if (order.state !== 'production') return ORDER_STATE_LABEL[order.state].tone;

  if (order.urgent) return 'urgent';
  if (overdue(order, today)) return 'late';
  return DUE_TONE[soon(order, today)];
}

function labelFor(order: JobOrder, today: string): string {
  if (order.state !== 'production') return ORDER_STATE_LABEL[order.state].label;
  if (order.urgent) return overdue(order, today) ? URGENT_OVERDUE_LABEL : strings.orders.urgent;
  if (overdue(order, today)) return strings.orders.overdue;
  const due = soon(order, today);
  return due === 'later' ? strings.orders.inProduction : DUE_LABEL[due];
}

function overdue(order: JobOrder, today: string): boolean {
  // Late as the server counts it: an order in production is not finished while any tray is not Ready.
  return isLate(order.deliveryDate, today, caseFinished(order));
}

/** Due today, due tomorrow, or later: what is left once a case is not overdue. */
function soon(order: JobOrder, today: string): Exclude<DueKey, 'overdue'> {
  const due = dueKey(order.deliveryDate, today);
  return due === 'overdue' ? 'later' : due;
}

/**
 * The order's drawer: the panel, and in it the case's own view.
 *
 * What the drawer holds was written here, and private to this screen, until
 * the request of 6 October 2026 (fifth), section 3, asked for the same view
 * where a case is opened in place on a doctor's or a patient's page. It is
 * `OrderCaseView` now, and this is only the panel Orders puts it in, titled
 * with the job order number. A case that has gone closes the drawer.
 */
function OrderDrawer({
  jobOrderNo,
  open,
  onClose,
}: {
  jobOrderNo: string | undefined;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Drawer open={open} title={jobOrderNo ?? ''} onClose={onClose}>
      <OrderCaseView jobOrderNo={jobOrderNo} onGone={onClose} />
    </Drawer>
  );
}
